import React, { PureComponent } from 'react';

import { I18n, translate } from 'components/translate';
import DateHelper, { shortFormatDate } from 'helpers/Date';
import { createSelectOptions } from 'helpers/Object';
import { inject } from 'mobx-react';
import { UserManagerService } from 'modules/userManager';
import {
  USER_TYPE,
  ENGAGEMENT_LABELS,
  USER_STATUS,
  REASONS_BLOCK
} from 'modules/userManager/constants';
import PropTypes from 'prop-types';

import DetailBoard from './DetailBoard';
import UserDetailService from './UserDetailService';

import { isToggleReasonModalEnabled } from '../usersList/actions/helpers/toggleReasonModal';

import './UserDetailData.scss';

@inject((stores) => ({
  locale: stores.UserStore.locale
}))
@translate()
export default class UserDetailData extends PureComponent {
  constructor(props) {
    super(props);

    const { t } = props;

    this.toggleLabel = { on: t('general.yes'), off: t('general.no') };
  }

  static propTypes = {
    locale: PropTypes.string,
    t: PropTypes.func,
    userData: PropTypes.object
  };

  state = { checked: false };

  getConcludedClassFormatted(beginValue, endValue) {
    return beginValue && endValue
      ? this.props.t('pagination.amount_from_total', {
          data: {
            amount: beginValue,
            total: endValue
          }
        })
      : '';
  }

  getUserType(userType) {
    const { t } = this.props;

    const typesLabels = ['BUYER', 'FREE', 'GUEST', 'IMPORTED'];
    const typesValues = Object.values(USER_TYPE).map((typeValue) =>
      t(typeValue)
    );
    const types = createSelectOptions(typesLabels, typesValues);

    return types.find((type) => type.value === userType).label;
  }

  renderAccessData() {
    const { userData } = this.props;
    const { BLOCKED_BY_OWNER, BLOCKED_BY_OWNER_V2 } = USER_STATUS;
    const blockedByOwner =
      userData?.userStatus === BLOCKED_BY_OWNER
        ? BLOCKED_BY_OWNER_V2
        : userData.userStatus;

    return (
      <div>
        <I18n label={UserManagerService.getStatusLabel(blockedByOwner)}></I18n>
      </div>
    );
  }

  renderReason() {
    const { userStatus, reasonBlocked, descriptionReasonBlocked } =
      this.props.userData;
    const { BLOCKED_BY_OWNER, CHANGED_BY_OWNER } = USER_STATUS;
    const { OTHERS } = REASONS_BLOCK;
    const isBlockedByOwner = userStatus === BLOCKED_BY_OWNER;
    const isOtherReason = reasonBlocked === OTHERS;

    if (!isToggleReasonModalEnabled() || reasonBlocked === CHANGED_BY_OWNER) {
      return null;
    }

    if (reasonBlocked && isBlockedByOwner) {
      return (
        <>
          {isOtherReason ? (
            <span>{descriptionReasonBlocked}</span>
          ) : (
            <I18n
              label={UserManagerService.getReasonLabel(reasonBlocked)}
            ></I18n>
          )}
        </>
      );
    }

    return null;
  }

  onToggleChange = async (event) => {
    const { userData } = this.props;
    const checked = event.target.value;
    const endpoint = checked ? 'addUser' : 'deleteUser';

    await UserDetailService.toggleWhitelistUserStatus(endpoint, {
      id: userData.id
    });
    this.setState({ checked });
  };

  get piracyData() {
    const { userData } = this.props;

    return userData.piracy || {};
  }

  get listData() {
    const { userData, t, locale } = this.props;

    if (!Object.keys(userData).length) {
      return [];
    }

    const listData = [
      {
        title: 'general.progress',
        information: `${Math.floor(userData.progress.completedPercentage)}%`
      },
      {
        title: 'users_manager.users_detail.concluded_class',
        information: this.getConcludedClassFormatted(
          userData.progress.completed,
          userData.progress.total
        )
      },
      {
        title: 'users_manager.deep_search.access.category',
        information: this.getUserType(userData.userType)
      },
      {
        title: 'users_import.manually.date.label',
        information: userData.userPurchaseDate,
        useInput: true
      },
      {
        title: 'users_manager.users_detail.first_login',
        information: DateHelper.formatDate(
          userData.userFirstAccessDate,
          shortFormatDate
        )
      },
      {
        title: 'general.last_access',
        information: DateHelper.formatDate(
          userData.userLastAccessDate,
          shortFormatDate
        )
      },
      {
        title: 'general.engagement',
        information: t(ENGAGEMENT_LABELS[userData.engagement])
      },
      {
        title: 'general.accesses',
        information: userData.userAccessCount
      },
      {
        title: 'users_manager.users_list.columns_manager.current_class',
        information: userData.currentPage || ''
      },
      {
        title: 'general.access',
        information: this.renderAccessData()
      },
      {
        title: 'general.reasons',
        information: this.renderReason()
      },
      {
        title: 'general.idiom',
        information: t(`supported_languages.${locale}`)
      }
    ];

    return listData;
  }

  renderEmptySlots() {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const nSlots = this.listData.length % 3;

    if (!nSlots) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const empty = Array.from({ length: 3 - nSlots });

    return empty.map((_, index) => (
      <div key={index} className="detail__board detail__board--empty" />
    ));
  }

  render() {
    const { userData } = this.props;

    return (
      <div className="user__detail__data__boards">
        {this.listData.map((data) => (
          <DetailBoard
            key={data.title}
            title={data.title}
            information={data.information}
            useInput={data.useInput}
            userAreaHash={userData.id}
          />
        ))}
        {this.renderEmptySlots()}
      </div>
    );
  }
}
